import React, { useState, useEffect } from 'react';
import { RiskCard, RiskCardBody } from './UiKit';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function MLPredictionCard({ assessmentId, autoTrigger = false }) {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoTriggered, setAutoTriggered] = useState(false);

  const generatePrediction = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/risk-assessment/ml/predict-risk`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assessment_id: assessmentId,
          features: {}
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setPrediction(data.prediction);
      } else {
        setError(data.message || 'Failed to generate prediction');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('ML Prediction Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-trigger prediction when component mounts
  useEffect(() => {
    if (autoTrigger && !prediction && !loading && !autoTriggered) {
      generatePrediction();
    }
  }, [autoTrigger, assessmentId]);

  const getRiskTierColor = (tier) => {
    switch (tier) {
      case 'A': return 'text-red-600 bg-red-100';
      case 'B': return 'text-yellow-600 bg-yellow-100';
      case 'C': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <RiskCard className="border-l-4 border-l-blue-500">
      <RiskCardBody className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h4 className="text-sm font-semibold text-gray-900">AI Risk Prediction</h4>
          </div>
          
          {!prediction && (
            <button
              onClick={generatePrediction}
              disabled={loading}
              className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Generating...' : 'Predict Risk'}
            </button>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">Analyzing assessment data...</span>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        {prediction && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {prediction.predicted_risk_score}
                </div>
                <div className="text-xs text-gray-600">Risk Score</div>
              </div>
              <div className="text-center">
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRiskTierColor(prediction.risk_tier)}`}>
                  Tier {prediction.risk_tier}
                </div>
                <div className="text-xs text-gray-600 mt-1">Risk Tier</div>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Confidence:</span>
              <span className={`font-medium ${getConfidenceColor(prediction.confidence_level)}`}>
                {Math.round(prediction.confidence_level * 100)}%
              </span>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Model: {prediction.model_version}</span>
              <span>{new Date(prediction.prediction_date).toLocaleDateString()}</span>
            </div>

            <div className="pt-2 border-t border-gray-200">
              <button
                onClick={generatePrediction}
                className="w-full px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Refresh Prediction
              </button>
            </div>
          </div>
        )}
      </RiskCardBody>
    </RiskCard>
  );
}
